import React from 'react'
import { formatPrice, formatPerc } from '../common/utils'
import { anySalesTax, buildRecurringPaymentText, calculatePerInvoiceAmounts, calculateSalesTax, calculateSelectedTotal, updateProgressBasedPaymentAmounts } from '../helpers/proposal'
import dayjs from 'dayjs'

const PerVisitPricingTypeName = 'Per Visit'
const TimeAndMaterialsPricingTypeName = 'Time and Materials'
const FlatRatePricingTypeName = 'Flat Rate'

const shouldShowEstimatedText = (lineItem) => lineItem.pricingTypeName === TimeAndMaterialsPricingTypeName || lineItem.pricingTypeName === FlatRatePricingTypeName

const Payment = ({lineItems, paymentScheduleOptions}) => {
  const perVisitLineItems = lineItems.filter(x => x.pricingTypeName === PerVisitPricingTypeName)
  const quotedOrTimeAndMaterialLineItems = lineItems.filter(x => x.pricingTypeName !== PerVisitPricingTypeName)
  const showSalesTax = anySalesTax(lineItems)

  const showProgressBased = quotedOrTimeAndMaterialLineItems.some(x => x.isSelected) && paymentScheduleOptions.quotedAndTimeAndMaterials.selectedOption === 'progress-based'
  const showIntervalBased = quotedOrTimeAndMaterialLineItems.some(x => x.isSelected) && paymentScheduleOptions.quotedAndTimeAndMaterials.selectedOption === 'interval-based'
  const showEstimated = quotedOrTimeAndMaterialLineItems.some(x => x.isSelected && shouldShowEstimatedText(x))
  const showPerVisit = perVisitLineItems.some(x => x.isSelected)

  return <div>
    {showProgressBased && <PaymentSection title="Progress-Based Invoicing Schedule">
        <ProgressBased
          lineItems={quotedOrTimeAndMaterialLineItems}
          paymentSchedule={paymentScheduleOptions.quotedAndTimeAndMaterials.progressBased}
          showSalesTax={showSalesTax}
          showEstimated={showEstimated}
        />
      </PaymentSection>}
    {showIntervalBased && <PaymentSection title="Interval-Based Invoicing Schedule">
        <IntervalBased
          lineItems={quotedOrTimeAndMaterialLineItems}
          intervalBased={paymentScheduleOptions.quotedAndTimeAndMaterials.intervalBased}
          showSalesTax={showSalesTax}
          showEstimated={showEstimated}
        />
      </PaymentSection>}
      {showPerVisit && <PaymentSection title="Per Visit Invoicing Schedule">
        <PerVisit
          lineItems={perVisitLineItems}
          perVisit={paymentScheduleOptions.perVisit}
          showSalesTax={showSalesTax}
        />
      </PaymentSection>}
  </div>
}

export default Payment

const PaymentSection = ({title, children}) => {
  return <div className="payment-section">
    <div className="header">{title}</div>
    <div className="content">{children}</div>
  </div>
}

const ProgressBased = ({lineItems, paymentSchedule, showSalesTax, showEstimated}) => {
  const totalPriceWithoutSalesTax = calculateSelectedTotal(lineItems)
  const salesTax = calculateSalesTax(lineItems)
  const totalPriceWithSalesTax = totalPriceWithoutSalesTax + salesTax
  const updatedPaymentSchedules = updateProgressBasedPaymentAmounts(lineItems, paymentSchedule, totalPriceWithoutSalesTax)

  return <>
    <div className='payment-section-row'>
      <div>
        Pertains to <strong>{lineItems.filter(x => x.isSelected).map(x => x.name).join(', ')}</strong>
      </div>
    </div>
    {updatedPaymentSchedules.map((x, i) => <div key={i} className="payment-section-row">
      <div>
        <div className="title">{x.title}</div>
        <div className="note">{x.note}</div>
      </div>
      <div>
        <div className="amount">{showEstimated ? 'Estimated' : ''} {formatPrice(x.amount)}</div>
        <div className="percentage">{formatPerc(x.percentage)}%</div>
      </div>
    </div>)}
    <div className="payment-section-row total">
      <strong>{showEstimated ? 'Estimated' : ''} Total</strong>
      <div>
        <div className="amount"><strong>{formatPrice(totalPriceWithSalesTax)}</strong></div>
        {showSalesTax && salesTax > 0 && <div className="note">Includes {formatPrice(salesTax)} sales tax</div>}
        {!showSalesTax && <div className="sales-tax-not-applicable">* Sales tax, if applicable, is not represented on this proposal</div>}
      </div>
    </div>
  </>
}

const buildStartingText = (recurring) => (!recurring.hideScheduleOnProposal && recurring.estimatedStartDate) ? `Starting ${dayjs(recurring.estimatedStartDate).format('MMM D, YYYY')}` : ''

const IntervalBased = ({lineItems, intervalBased, showSalesTax, showEstimated}) => {
  const {perInvoice, salesTaxPerInvoice, totalPriceWithSalesTax } = calculatePerInvoiceAmounts(lineItems, intervalBased.totalPayments)

  return <>
    <div className='payment-section-row'>
      <div>
        Pertains to <strong>{lineItems.filter(x => x.isSelected).map(x => x.name).join(', ')}</strong>
      </div>
    </div>
    <div className="payment-section-row">
      <div>
        <div>{buildRecurringPaymentText(intervalBased.recurring)}</div>
        <div className="note">{buildStartingText(intervalBased.recurring)}</div>
      </div>
      <div>
        <div className="amount"><span className="note">{intervalBased.totalPayments} payment{intervalBased.totalPayments > 1 ? 's' : ''} of</span> {showEstimated ? 'estimated' : ''} {formatPrice(perInvoice)}</div>
        {showSalesTax && <div className="note right">Includes {formatPrice(salesTaxPerInvoice)} sales tax</div>}
      </div>
    </div>
    <div className="payment-section-row total">
      <strong>{showEstimated ? 'Estimated' : ''} Total</strong>
      <div>
        <div className="amount"><strong>{formatPrice(totalPriceWithSalesTax)}</strong></div>
        {!showSalesTax && <div className="sales-tax-not-applicable">* Sales tax, if applicable, is not represented on this proposal</div>}
      </div>
    </div>
  </>
}

const PerVisit = ({perVisit, lineItems, showSalesTax}) => {
  const selected = lineItems.filter(x => x.isSelected)
  const displayBottomSection = !perVisit.recurring.hideScheduleOnProposal || !showSalesTax

  return <>
    {selected.map(x => <div key={x.id} className="payment-section-row">
      <div><strong>{x.name}</strong></div>
      <div>
        <div className="amount">{formatPrice(x.quotedPrice + x.estimatedSalesTax)} per visit</div>
        {showSalesTax && <div className="note">Includes {formatPrice(x.estimatedSalesTax)} sales tax</div> }
      </div>
    </div>)}
    {displayBottomSection && <div className="payment-section-row">
      <div>
        <div>{buildRecurringPaymentText(perVisit.recurring)}</div>
        <div className="note">{buildStartingText(perVisit.recurring)}</div>
      </div>
      {!showSalesTax && <div className="sales-tax-not-applicable">* Sales tax, if applicable, is not represented on this proposal</div>}
    </div>}
  </>
}